import { useQuery } from '@tanstack/react-query';

import {
  CompanyLookup,
  CreateUserType,
  ListItemLookup,
  ListPagedResult,
  PayrollServiceProviderLookup,
  RoleLookup,
} from '@xemplo/gp-types';
import { useHttpClient } from '@xemplo/http';

import { useUsersUrls } from './use-users-urls';
import {
  mapCompaniesToLookup,
  mapProvidersToLookup,
  mapRolesToLookup,
} from './users-lookup-query.helper';

export function useGetRoles(userType: CreateUserType) {
  const { client } = useHttpClient();
  const { applicationApiUrl, rolesUrl } = useUsersUrls();

  const getRolesUrl = rolesUrl(userType);
  return useQuery<ListItemLookup<RoleLookup>[]>({
    queryKey: [getRolesUrl],
    queryFn: ({ signal }) =>
      client
        .get<ListPagedResult<RoleLookup>>(getRolesUrl, { signal })
        .then((resp) => resp.data)
        .then(mapRolesToLookup),
    enabled: !!applicationApiUrl,
  });
}

export function useGetAssignRoleData(userType: CreateUserType) {
  const { client } = useHttpClient();
  const { applicationApiUrl, providersUrl, companyUrl, rolesUrl } = useUsersUrls();

  const providers = useQuery<ListItemLookup<PayrollServiceProviderLookup>[]>({
    queryKey: [providersUrl],
    queryFn: ({ signal }) =>
      client
        .get<ListPagedResult<PayrollServiceProviderLookup>>(providersUrl, { signal })
        .then((resp) => resp.data)
        .then(mapProvidersToLookup),
    enabled: !!applicationApiUrl && userType === CreateUserType.PayrollUser,
  });

  const companies = useQuery<ListItemLookup<CompanyLookup>[]>({
    queryKey: [companyUrl],
    queryFn: ({ signal }) =>
      client
        .get<ListPagedResult<CompanyLookup>>(companyUrl, { signal })
        .then((resp) => resp.data)
        .then(mapCompaniesToLookup),
    enabled: !!applicationApiUrl && userType === CreateUserType.CompanyUser,
  });

  const getRolesUrl = rolesUrl(userType);
  const roles = useQuery<ListItemLookup<RoleLookup>[]>({
    queryKey: [getRolesUrl],
    queryFn: ({ signal }) =>
      client
        .get<ListPagedResult<RoleLookup>>(getRolesUrl, { signal })
        .then((resp) => resp.data)
        .then(mapRolesToLookup),
    enabled: !!applicationApiUrl,
  });

  const isLoading = providers.isLoading || companies.isLoading || roles.isLoading;
  return { providers, companies, roles, isLoading };
}
